import { c as _c } from "react/compiler-runtime";
import { Field, useFormikContext } from 'formik';
import React, { ReactElement, memo } from 'react';
import { InputDataAttributes } from '@spa/components/form-group/InputDataAttributes';
import { InputMode } from '@spa/components/form-group/InputMode';
export interface MaskConfig {
  /** Mask template where '_' marks an editable slot, e.g. '__.__.____' or '____-____-____-____' */
  readonly mask: string;
  /** Whether a single typed character may be written into an editable slot */
  readonly isAllowedChar: (char: string) => boolean;
  /** Character class (regex body) of allowed slot characters, e.g. '0-9' or 'A-Za-z0-9' */
  readonly allowedCharClass: string;
  readonly inputMode: InputMode;
  readonly pattern?: string;
  /** Key that triggers slot formatting (e.g. '.' for the date mask) */
  readonly formatKey?: string;
  readonly onFormatKey?: (value: string, cursor: number) => {
    value: string;
    cursor: number;
  };
  readonly formatOnBlur?: (value: string) => string;
}
export interface Props {
  readonly error?: string;
  readonly errorBtn?: ReactElement;
  readonly name: string;
  readonly placeholder?: string;
  readonly value?: string;
  readonly isReadOnly?: boolean;
  readonly isDisabled?: boolean;
  readonly required?: boolean;
  readonly autoComplete?: string;
  readonly inputDataAttributes?: InputDataAttributes;
  readonly config: MaskConfig;
}
const allowedControlKeys = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Home', 'End', 'Shift', 'Control', 'Alt', 'Meta']);
const escapeRegExpChar = (char: string): string => char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const getEditableIndexes = (mask: string): Array<number> => Array.from(mask).flatMap((char: string, index: number): Array<number> => char === '_' ? [index] : []);
const buildConformanceRegex = (config: MaskConfig): RegExp => new RegExp(`^${Array.from(config.mask).map((char: string): string => char === '_' ? `[${config.allowedCharClass}_]` : escapeRegExpChar(char)).join('')}$`);
function MaskedInputComponent(props) {
  const $ = _c(102);
  const formik = useFormikContext();
  const selectionRef = React.useRef(null);
  const focusedViaMouseRef = React.useRef(false);
  let config;
  let fieldProps;
  let inputDataAttributes;
  let name;
  let required;
  let value;
  if ($[0] !== props) {
    ({
      value,
      name,
      required,
      inputDataAttributes,
      config,
      ...fieldProps
    } = props);
    $[0] = props;
    $[1] = config;
    $[2] = fieldProps;
    $[3] = inputDataAttributes;
    $[4] = name;
    $[5] = required;
    $[6] = value;
  } else {
    config = $[1];
    fieldProps = $[2];
    inputDataAttributes = $[3];
    name = $[4];
    required = $[5];
    value = $[6];
  }
  const {
    mask,
    isAllowedChar
  } = config;
  let t0;
  if ($[7] !== mask) {
    t0 = getEditableIndexes(mask);
    $[7] = mask;
    $[8] = t0;
  } else {
    t0 = $[8];
  }
  const editableIndexes = t0;
  let t1;
  if ($[9] !== config) {
    t1 = buildConformanceRegex(config);
    $[9] = config;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  const conformanceRegex = t1;
  let t2;
  if ($[11] !== isAllowedChar) {
    t2 = value_0 => value_0.length > 0 && Array.from(value_0).every(isAllowedChar);
    $[11] = isAllowedChar;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const isAllowedCharsOnly = t2;
  let t3;
  if ($[13] !== conformanceRegex) {
    t3 = v => conformanceRegex.test(v);
    $[13] = conformanceRegex;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const isMaskedValue = t3;
  let t4;
  if ($[15] !== editableIndexes) {
    t4 = index => editableIndexes.includes(index);
    $[15] = editableIndexes;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const isEditableIndex = t4;
  const setCharAt = _temp;
  let t5;
  if ($[17] !== isEditableIndex || $[18] !== mask.length) {
    t5 = from => {
      let nextEditablePosition = from;
      while (nextEditablePosition < mask.length && !isEditableIndex(nextEditablePosition)) {
        nextEditablePosition++;
      }
      return nextEditablePosition;
    };
    $[17] = isEditableIndex;
    $[18] = mask.length;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const getNextEditable = t5;
  let t6;
  if ($[20] !== isEditableIndex) {
    t6 = from_0 => {
      let prevEditablePosition = from_0;
      while (prevEditablePosition >= 0 && !isEditableIndex(prevEditablePosition)) {
        prevEditablePosition--;
      }
      return prevEditablePosition;
    };
    $[20] = isEditableIndex;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const getPrevEditable = t6;
  let t7;
  if ($[22] !== getNextEditable) {
    t7 = value_1 => {
      const firstUnderscore = value_1.indexOf("_");
      return firstUnderscore !== -1 ? firstUnderscore : getNextEditable(0);
    };
    $[22] = getNextEditable;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const firstEditablePos = t7;
  let t8;
  if ($[24] !== mask) {
    t8 = v_0 => v_0.trim() === "" || v_0 === mask;
    $[24] = mask;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const isEmptyValue = t8;
  let t9;
  if ($[26] !== isEditableIndex) {
    t9 = (start, end) => {
      const editablePositions = [];
      for (let position = start; position < end; position++) {
        if (isEditableIndex(position)) {
          editablePositions.push(position);
        }
      }
      return editablePositions;
    };
    $[26] = isEditableIndex;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  const clampSelectionToEditable = t9;
  let t10;
  if ($[28] !== clampSelectionToEditable) {
    t10 = (current, start_0, end_0) => {
      let nextValue = current;
      const editablePositions_0 = clampSelectionToEditable(start_0, end_0);
      for (let index_1 = 0; index_1 < editablePositions_0.length; index_1++) {
        const position_0 = editablePositions_0[index_1];
        nextValue = setCharAt(nextValue, position_0, "_");
      }
      return nextValue;
    };
    $[28] = clampSelectionToEditable;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const maskSelection = t10;
  let t11;
  if ($[30] !== clampSelectionToEditable || $[31] !== getNextEditable || $[32] !== mask.length || $[33] !== maskSelection) {
    t11 = (current_0, start_1, end_1, chars) => {
      let next = maskSelection(current_0, start_1, end_1);
      const editableSelection = clampSelectionToEditable(start_1, end_1);
      let writePositions = editableSelection.length > 0 ? editableSelection : [];
      if (writePositions.length === 0) {
        const position_1 = getNextEditable(start_1);
        if (position_1 < mask.length) {
          writePositions = [position_1];
        }
      }
      let charIndex = 0;
      for (let index_2 = 0; index_2 < writePositions.length; index_2++) {
        const position_2 = writePositions[index_2];
        if (charIndex < chars.length) {
          next = setCharAt(next, position_2, chars[charIndex]);
          charIndex++;
        }
      }
      let lastPosition = writePositions.length > 0 ? writePositions[Math.min(charIndex, writePositions.length) - 1] : start_1;
      if (charIndex === 0) {
        lastPosition = start_1;
      }
      const nextPosition = getNextEditable(lastPosition + 1);
      const cursor = nextPosition >= mask.length ? Math.min(lastPosition + 1, mask.length) : nextPosition;
      return {
        nextValue: next,
        nextCursor: cursor
      };
    };
    $[30] = clampSelectionToEditable;
    $[31] = getNextEditable;
    $[32] = mask.length;
    $[33] = maskSelection;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  const replaceSelectionWithChars = t11;
  let t12;
  if ($[35] !== getNextEditable || $[36] !== isEditableIndex || $[37] !== mask.length) {
    t12 = (current_1, cursor_0, chars_0) => {
      let next_0 = current_1;
      let position_3 = isEditableIndex(cursor_0) ? cursor_0 : getNextEditable(cursor_0);
      let charIndex_0 = 0;
      let lastWrittenPos = position_3;
      while (position_3 < mask.length && charIndex_0 < chars_0.length) {
        if (isEditableIndex(position_3)) {
          next_0 = setCharAt(next_0, position_3, chars_0[charIndex_0]);
          lastWrittenPos = position_3;
          charIndex_0++;
        }
        position_3 = getNextEditable(position_3 + 1);
      }
      const nextPos = getNextEditable(lastWrittenPos + 1);
      const nextCursor = nextPos >= mask.length ? Math.min(lastWrittenPos + 1, mask.length) : nextPos;
      return {
        nextValue: next_0,
        nextCursor
      };
    };
    $[35] = getNextEditable;
    $[36] = isEditableIndex;
    $[37] = mask.length;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  const insertCharsFromCursor = t12;
  const maskedValue = value ?? mask;
  const moveCursor = _temp2;
  let t13;
  if ($[39] !== formik || $[40] !== name) {
    t13 = async (el, nextVal, cursor_2) => {
      await formik.setFieldValue(name, nextVal);
      moveCursor(el, cursor_2);
    };
    $[39] = formik;
    $[40] = name;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  const setFieldAndCursor = t13;
  const t14 = required || undefined;
  let t15;
  if ($[42] !== isAllowedCharsOnly) {
    t15 = event => {
      const data = event.data ?? "";
      if (data && !isAllowedCharsOnly(data)) {
        event.preventDefault();
      }
    };
    $[42] = isAllowedCharsOnly;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== config.allowedCharClass || $[45] !== editableIndexes.length || $[46] !== insertCharsFromCursor || $[47] !== maskedValue || $[48] !== replaceSelectionWithChars || $[49] !== setFieldAndCursor) {
    t16 = async event_0 => {
      event_0.preventDefault();
      const {
        currentTarget: element_0,
        clipboardData
      } = event_0;
      const {
        selectionStart,
        selectionEnd
      } = element_0;
      const pasted = clipboardData.getData("text");
      const chars_1 = pasted.trim().replace(new RegExp(`[^${config.allowedCharClass}]`, "g"), "").slice(0, editableIndexes.length);
      const start_2 = selectionStart ?? 0;
      const end_2 = selectionEnd ?? 0;
      const selectionActive = end_2 > start_2;
      if (chars_1.length > 0) {
        if (selectionActive) {
          const {
            nextValue: nextValue_0,
            nextCursor: nextCursor_0
          } = replaceSelectionWithChars(maskedValue, start_2, end_2, chars_1);
          await setFieldAndCursor(element_0, nextValue_0, nextCursor_0);
        } else {
          const {
            nextValue: nextValue_1,
            nextCursor: nextCursor_1
          } = insertCharsFromCursor(maskedValue, start_2, chars_1);
          await setFieldAndCursor(element_0, nextValue_1, nextCursor_1);
        }
      }
    };
    $[44] = config.allowedCharClass;
    $[45] = editableIndexes.length;
    $[46] = insertCharsFromCursor;
    $[47] = maskedValue;
    $[48] = replaceSelectionWithChars;
    $[49] = setFieldAndCursor;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  let t17;
  let t18;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = t19 => {
      const {
        currentTarget: t20
      } = t19;
      const {
        selectionStart: selectionStart_0,
        selectionEnd: selectionEnd_0
      } = t20;
      const start_3 = selectionStart_0 ?? 0;
      const end_3 = selectionEnd_0 ?? 0;
      selectionRef.current = {
        start: start_3,
        end: end_3
      };
    };
    t18 = () => {
      focusedViaMouseRef.current = true;
    };
    $[51] = t17;
    $[52] = t18;
  } else {
    t17 = $[51];
    t18 = $[52];
  }
  let t19;
  if ($[53] !== firstEditablePos || $[54] !== isEmptyValue || $[55] !== mask || $[56] !== maskedValue || $[57] !== setFieldAndCursor) {
    t19 = async t20 => {
      const {
        currentTarget
      } = t20;
      const viaMouse = focusedViaMouseRef.current;
      focusedViaMouseRef.current = false;
      if (isEmptyValue(maskedValue)) {
        await setFieldAndCursor(currentTarget, mask, 0);
      } else {
        if (!viaMouse) {
          const position_4 = firstEditablePos(maskedValue);
          moveCursor(currentTarget, position_4);
        }
      }
    };
    $[53] = firstEditablePos;
    $[54] = isEmptyValue;
    $[55] = mask;
    $[56] = maskedValue;
    $[57] = setFieldAndCursor;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  let t20;
  if ($[59] !== isEmptyValue || $[60] !== maskedValue) {
    t20 = t21 => {
      const {
        currentTarget: currentTarget_0
      } = t21;
      if (isEmptyValue(maskedValue)) {
        moveCursor(currentTarget_0, 0);
      }
    };
    $[59] = isEmptyValue;
    $[60] = maskedValue;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  let t21;
  if ($[62] !== config || $[63] !== formik || $[64] !== getNextEditable || $[65] !== getPrevEditable || $[66] !== isAllowedChar || $[67] !== isEditableIndex || $[68] !== mask.length || $[69] !== maskSelection || $[70] !== maskedValue || $[71] !== name || $[72] !== replaceSelectionWithChars || $[73] !== setFieldAndCursor) {
    t21 = async event_1 => {
      const {
        currentTarget: element_1,
        ctrlKey,
        metaKey,
        key
      } = event_1;
      const {
        selectionStart: selectionStart_1,
        selectionEnd: selectionEnd_1
      } = element_1;
      const currentPosition = selectionStart_1 ?? 0;
      const endSel = selectionEnd_1 ?? 0;
      const selectionActive_0 = endSel > currentPosition;
      const isShortcut = ctrlKey || metaKey;
      const shortcutKey = key.toLowerCase();
      const isTextEditingShortcut = isShortcut && (shortcutKey === "a" || shortcutKey === "c" || shortcutKey === "v");
      if (key === "Backspace") {
        event_1.preventDefault();
        if (selectionActive_0) {
          const nextVal_0 = maskSelection(maskedValue, currentPosition, endSel);
          await setFieldAndCursor(element_1, nextVal_0, currentPosition);
        } else {
          const prev = getPrevEditable(currentPosition - 1);
          if (prev >= 0) {
            const nextVal_1 = setCharAt(maskedValue, prev, "_");
            await setFieldAndCursor(element_1, nextVal_1, prev);
          }
        }
      } else {
        if (key === "Delete") {
          event_1.preventDefault();
          if (selectionActive_0) {
            const nextVal_2 = maskSelection(maskedValue, currentPosition, endSel);
            await setFieldAndCursor(element_1, nextVal_2, currentPosition);
          } else {
            let position_5 = isEditableIndex(currentPosition) ? currentPosition : getNextEditable(currentPosition);
            if (position_5 < mask.length) {
              while (position_5 < mask.length && (!isEditableIndex(position_5) || maskedValue[position_5] === "_")) {
                position_5++;
              }
              if (position_5 < mask.length) {
                const nextVal_3 = setCharAt(maskedValue, position_5, "_");
                await setFieldAndCursor(element_1, nextVal_3, currentPosition);
              }
            }
          }
        } else {
          if (key.length === 1 && !isShortcut && isAllowedChar(key)) {
            event_1.preventDefault();
            if (selectionActive_0) {
              const {
                nextValue: nextValue_2,
                nextCursor: nextCursor_2
              } = replaceSelectionWithChars(maskedValue, currentPosition, endSel, key);
              await setFieldAndCursor(element_1, nextValue_2, nextCursor_2);
            } else {
              const position_6 = isEditableIndex(currentPosition) ? currentPosition : getNextEditable(currentPosition);
              if (position_6 < mask.length) {
                const nextVal_4 = setCharAt(maskedValue, position_6, key);
                const nextPos_0 = getNextEditable(position_6 + 1);
                const finalPos = nextPos_0 >= mask.length ? position_6 + 1 : nextPos_0;
                await setFieldAndCursor(element_1, nextVal_4, finalPos);
              }
            }
          } else {
            if (config.formatKey && config.onFormatKey && key === config.formatKey) {
              event_1.preventDefault();
              const {
                value: formatted,
                cursor: nextStart
              } = config.onFormatKey(maskedValue, currentPosition);
              if (formatted !== maskedValue) {
                await formik.setFieldValue(name, formatted);
              }
              moveCursor(element_1, nextStart);
            } else {
              if (!allowedControlKeys.has(key) && !isTextEditingShortcut) {
                event_1.preventDefault();
              }
            }
          }
        }
      }
    };
    $[62] = config;
    $[63] = formik;
    $[64] = getNextEditable;
    $[65] = getPrevEditable;
    $[66] = isAllowedChar;
    $[67] = isEditableIndex;
    $[68] = mask.length;
    $[69] = maskSelection;
    $[70] = maskedValue;
    $[71] = name;
    $[72] = replaceSelectionWithChars;
    $[73] = setFieldAndCursor;
    $[74] = t21;
  } else {
    t21 = $[74];
  }
  let t22;
  if ($[75] !== config || $[76] !== formik || $[77] !== mask || $[78] !== maskedValue || $[79] !== name) {
    t22 = async () => {
      let nextVal_5 = maskedValue;
      if (nextVal_5 === mask) {
        await formik.setFieldValue(name, "");
      } else {
        if (config.formatOnBlur) {
          nextVal_5 = config.formatOnBlur(nextVal_5);
          if (nextVal_5 !== maskedValue) {
            await formik.setFieldValue(name, nextVal_5);
          }
        }
      }
      await formik.setFieldTouched(name, true);
    };
    $[75] = config;
    $[76] = formik;
    $[77] = mask;
    $[78] = maskedValue;
    $[79] = name;
    $[80] = t22;
  } else {
    t22 = $[80];
  }
  let t23;
  if ($[81] !== formik || $[82] !== isMaskedValue || $[83] !== maskedValue || $[84] !== name) {
    t23 = async t24 => {
      const {
        currentTarget: t25
      } = t24;
      const {
        value: value_2
      } = t25;
      const nextRaw = value_2;
      if (nextRaw !== "" && !isMaskedValue(nextRaw)) {
        await formik.setFieldValue(name, maskedValue);
      }
    };
    $[81] = formik;
    $[82] = isMaskedValue;
    $[83] = maskedValue;
    $[84] = name;
    $[85] = t23;
  } else {
    t23 = $[85];
  }
  let t24;
  if ($[86] !== config.inputMode || $[87] !== config.pattern || $[88] !== fieldProps || $[89] !== inputDataAttributes || $[90] !== maskedValue || $[91] !== name || $[92] !== required || $[93] !== t14 || $[94] !== t15 || $[95] !== t16 || $[96] !== t19 || $[97] !== t20 || $[98] !== t21 || $[99] !== t22 || $[100] !== t23) {
    t24 = <Field {...inputDataAttributes} {...fieldProps} name={name} required={required} value={maskedValue} inputMode={config.inputMode} pattern={config.pattern} type="text" aria-required={t14} onCompositionEnd={t15} onPaste={t16} onSelect={t17} onMouseDown={t18} onFocus={t19} onClick={t20} onKeyDown={t21} onBlur={t22} onChange={t23} />;
    $[86] = config.inputMode;
    $[87] = config.pattern;
    $[88] = fieldProps;
    $[89] = inputDataAttributes;
    $[90] = maskedValue;
    $[91] = name;
    $[92] = required;
    $[93] = t14;
    $[94] = t15;
    $[95] = t16;
    $[96] = t19;
    $[97] = t20;
    $[98] = t21;
    $[99] = t22;
    $[100] = t23;
    $[101] = t24;
  } else {
    t24 = $[101];
  }
  return t24;
}
function _temp2(element, cursor_1) {
  requestAnimationFrame(() => element.setSelectionRange(cursor_1, cursor_1));
}
function _temp(str, index_0, ch) {
  return str.substring(0, index_0) + ch + str.substring(index_0 + 1);
}
export default memo(MaskedInputComponent);